/*! napc-cart-control.js
 *  ONE copy of the live-cart manager + cart control for the whole page
 *  (previously the full script was printed inside every product card).
 *  Behaviour is unchanged; differences:
 *   - document-level listeners are registered once, not once per card
 *   - the first /cart.js sync runs after window load + idle, so it never
 *     competes with the LCP image or main-thread work
 */
(function () {
  'use strict';

  if (window.NAPCLiveCart) return;

  function whenIdle(fn) {
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2500 });
    else setTimeout(fn, 300);
  }

  function routeUrl(routeKey, shopifyPath, fallback) {
    if (window.routes && window.routes[routeKey]) return window.routes[routeKey] + '.js';
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + shopifyPath;
    }
    return fallback;
  }

  /*
   * =========================================================
   * GLOBAL NAPC LIVE CART MANAGER
   * =========================================================
   */
  window.NAPCLiveCart = {
    initialized: false,
    instances: new Set(),
    syncTimer: null,
    syncing: false,
    lastCart: null,

    register: function (instance) {
      this.instances.add(instance);
      if (this.lastCart) instance.syncFromCart(this.lastCart);
    },

    unregister: function (instance) {
      this.instances.delete(instance);
    },

    getCart: function () {
      var cartUrl = window.routes && window.routes.cart_url
        ? window.routes.cart_url + '.js'
        : (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root + 'cart.js'
            : '/cart.js');

      return fetch(cartUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      }).then(function (response) {
        if (!response.ok) throw new Error('Unable to get cart.');
        return response.json();
      });
    },

    findCartItem: function (cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return null;
      var targetId = String(variantId);
      for (var i = 0; i < cart.items.length; i++) {
        if (String(cart.items[i].variant_id) === targetId) return cart.items[i];
      }
      return null;
    },

    syncInstances: function (cart) {
      if (!cart) return;
      this.lastCart = cart;

      this.instances.forEach(function (instance) {
        if (!instance.rootElement || !document.documentElement.contains(instance.rootElement)) {
          window.NAPCLiveCart.instances.delete(instance);
          return;
        }
        instance.syncFromCart(cart);
      });

      this.updateCartBubble(cart);
    },

    updateCartBubble: function (cart) {
      if (!cart) return;
      var count = cart.item_count || 0;

      document.querySelectorAll('.cmh__cart-count').forEach(function (element) {
        element.textContent = count;
        element.setAttribute('data-cart-count', count);
        if (count > 0) element.removeAttribute('hidden');
        else element.setAttribute('hidden', '');
      });
    },

    setVariantQuantity: function (variantId, quantity) {
      var self = this;
      this.lastCart = this.lastCart || null;
      var targetVariantId = String(variantId);

      this.instances.forEach(function (instance) {
        if (!instance.rootElement || !document.documentElement.contains(instance.rootElement)) {
          self.instances.delete(instance);
          return;
        }
        if (String(instance.variantId) === targetVariantId) {
          instance.setQuantity(quantity, true);
        }
      });

      if (this.lastCart && Array.isArray(this.lastCart.items)) {
        var found = false;
        this.lastCart.items.forEach(function (item) {
          if (String(item.variant_id) === targetVariantId) {
            item.quantity = quantity;
            found = true;
          }
        });

        if (this.lastCart.item_count != null && !found && quantity > 0) {
          this.lastCart.item_count = Number(this.lastCart.item_count) + quantity;
        }
        this.updateCartBubble(this.lastCart);
      }
    },

    sync: function (immediate) {
      var self = this;
      if (!immediate) {
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(function () { self.sync(true); }, 150);
        return;
      }

      if (this.syncing) return;
      this.syncing = true;

      this.getCart()
        .then(function (cart) { self.syncInstances(cart); })
        .catch(function (error) { console.error('[NAPC] Cart sync error:', error); })
        .finally(function () { self.syncing = false; });
    },

    init: function () {
      if (this.initialized) return;
      this.initialized = true;
      var self = this;

      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
            if (event && event.cartData) self.syncInstances(event.cartData);
            self.sync();
          });
        }
      } catch (error) {
        console.warn('[NAPC] Unable to subscribe to cartUpdate:', error);
      }

      document.addEventListener('cart:updated', function (event) {
        if (event && event.detail && event.detail.cart) self.syncInstances(event.detail.cart);
        self.sync();
      });

      // ONE variant-change listener for all cards (was one per card)
      document.addEventListener('fbt:variant-change', function (event) {
        if (!event.detail || !event.detail.variant) return;
        var variant = event.detail.variant;
        self.instances.forEach(function (instance) {
          if (variant.product_id && String(variant.product_id) !== String(instance.productId)) return;
          instance.setVariant(variant);
        });
      });

      // First cart sync: after load + idle, so it can't delay LCP
      var start = function () { whenIdle(function () { self.sync(true); }); };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }
  };


  /*
   * =========================================================
   * NAPC CART CONTROL
   * =========================================================
   */
  class NapcCartControl {
    constructor(rootElement) {
      this.rootElement = rootElement;
      this.variantId = rootElement.dataset.napcVariantId || rootElement.dataset.variantId;
      this.productId = rootElement.dataset.napcProductId || rootElement.dataset.productId;

      this.formElement = rootElement.querySelector('form[data-type="add-to-cart-form"]');
      this.addButton = rootElement.querySelector('[data-napc-add-button]');
      this.buttonText = rootElement.querySelector('.napc-cart-control__button-text');
      this.loadingText = rootElement.querySelector('.napc-cart-control__loading-text');
      this.formQuantity = rootElement.querySelector('[data-napc-form-quantity]');
      this.quantityWrapper = rootElement.querySelector('[data-napc-quantity-wrapper]');
      this.quantityElement = rootElement.querySelector('[data-napc-quantity-value]');
      this.minusButton = rootElement.querySelector('[data-napc-minus-button]');
      this.plusButton = rootElement.querySelector('[data-napc-plus-button]');

      this.quantity = 0;
      this.busy = false;
      this.lastRequestTimestamp = 0;
      this.debounceTimer = null;

      this.bindEvents();
      window.NAPCLiveCart.register(this);
    }

    setVariant(variant) {
      if (!variant || !variant.id) return;
      var newVariantId = String(variant.id);

      this.variantId = newVariantId;
      this.rootElement.dataset.napcVariantId = newVariantId;
      this.rootElement.setAttribute('data-napc-variant-id', newVariantId);
      this.rootElement.dataset.available = String(!!variant.available);

      if (this.formElement) {
        var variantInput = this.formElement.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = newVariantId;
          variantInput.setAttribute('value', newVariantId);
        }
      }

      if (!variant.available) {
        this.quantity = 0;
        if (this.formElement) this.formElement.hidden = false;
        if (this.quantityWrapper) this.quantityWrapper.hidden = true;
        if (this.addButton) {
          this.addButton.disabled = true;
          this.addButton.classList.add('napc-cart-control__button--sold-out');
        }
        if (this.buttonText) this.buttonText.textContent = 'Sold Out';
        this.rootElement.setAttribute('data-in-cart', 'false');
        return;
      }

      if (this.addButton) {
        this.addButton.disabled = false;
        this.addButton.classList.remove('napc-cart-control__button--sold-out');
      }
      if (this.buttonText) this.buttonText.textContent = 'Add to Cart';

      var self = this;
      window.NAPCLiveCart.getCart().then(function (cart) {
        if (!cart) return;
        window.NAPCLiveCart.lastCart = cart;
        var item = window.NAPCLiveCart.findCartItem(cart, newVariantId);
        self.setQuantity(item ? item.quantity : 0, true);
        window.NAPCLiveCart.updateCartBubble(cart);
      }).catch(function (error) {
        console.error('[NAPC] Variant cart sync error:', error);
      });
    }

    getCartElement() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    getSectionsToRender() {
      var cartElement = this.getCartElement();
      if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
        return cartElement.getSectionsToRender().map(function (section) { return section.id; });
      }
      return ['cart-icon-bubble'];
    }

    bindEvents() {
      var self = this;

      if (this.formElement) {
        this.formElement.addEventListener('submit', function (event) {
          event.preventDefault();
          event.stopPropagation();
          self.addToCart();
          return false;
        });
      }

      if (this.minusButton) {
        this.minusButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          if (self.busy) return;
          self.decreaseQuantity();
        });
      }

      if (this.plusButton) {
        this.plusButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          if (self.busy) return;
          self.increaseQuantity();
        });
      }
    }

    addToCart() {
      if (!this.variantId || this.rootElement.dataset.available === 'false') return;

      var currentTimestamp = Date.now();
      this.lastRequestTimestamp = currentTimestamp;

      // Optimistic UI
      var prevQty = this.quantity;
      this.setQuantity(prevQty + 1, true);
      window.NAPCLiveCart.setVariantQuantity(this.variantId, this.quantity);

      var self = this;
      var addUrl = window.routes && window.routes.cart_add_url
        ? window.routes.cart_add_url + '.js'
        : (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root + 'cart/add.js'
            : '/cart/add.js');

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(this.variantId), quantity: 1 }],
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().catch(function () { return {}; }).then(function (data) {
              throw new Error(data.description || data.message || 'Unable to add product to cart.');
            });
          }
          return response.json();
        })
        .then(function (response) {
          if (currentTimestamp < self.lastRequestTimestamp) return;
          if (response.status) {
            throw new Error(response.description || response.message || 'Unable to add product to cart.');
          }

          self.setQuantityFromResponse(response, prevQty + 1);
          window.NAPCLiveCart.setVariantQuantity(self.variantId, self.quantity);
          self.renderCart(response);
          self.publishUpdate(response);
        })
        .catch(function (error) {
          if (currentTimestamp < self.lastRequestTimestamp) return;
          // Rollback on failure
          self.setQuantity(prevQty, true);
          window.NAPCLiveCart.sync(true);
          console.error('[NAPC] Add to cart error:', error);
        });
    }

    increaseQuantity() {
      this.changeQuantity(this.quantity + 1);
    }

    decreaseQuantity() {
      this.changeQuantity(Math.max(0, this.quantity - 1));
    }

    changeQuantity(nextQuantity) {
      if (!this.variantId) return;

      nextQuantity = Math.max(0, parseInt(nextQuantity, 10) || 0);
      var prevQuantity = this.quantity;

      // Immediate optimistic visual update
      this.setQuantity(nextQuantity, true);
      window.NAPCLiveCart.setVariantQuantity(this.variantId, nextQuantity);

      var currentTimestamp = Date.now();
      this.lastRequestTimestamp = currentTimestamp;
      var self = this;

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(function () {
        self.setBusy(true);

        var changeUrl = window.routes && window.routes.cart_change_url
          ? window.routes.cart_change_url + '.js'
          : (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root + 'cart/change.js'
              : '/cart/change.js');

        fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: String(self.variantId),
            quantity: nextQuantity,
            sections: self.getSectionsToRender(),
            sections_url: window.location.pathname
          })
        })
          .then(function (response) { return response.json(); })
          .then(function (response) {
            if (currentTimestamp < self.lastRequestTimestamp) return;
            if (response.status) {
              throw new Error(response.description || response.message || 'Unable to update cart.');
            }

            self.setQuantityFromResponse(response, nextQuantity);
            self.renderCart(response);
            self.publishUpdate(response);
            window.NAPCLiveCart.syncInstances(response);
          })
          .catch(function (error) {
            if (currentTimestamp < self.lastRequestTimestamp) return;
            // Rollback on network or server error
            self.setQuantity(prevQuantity, true);
            window.NAPCLiveCart.sync(true);
            console.error('[NAPC] Quantity update error:', error);
          })
          .finally(function () {
            if (currentTimestamp >= self.lastRequestTimestamp) self.setBusy(false);
          });
      }, 50); // micro-debounce to batch rapid clicks
    }

    syncFromCart(cart) {
      if (!cart || !this.variantId) return;
      var item = window.NAPCLiveCart.findCartItem(cart, this.variantId);
      this.setQuantity(item ? item.quantity : 0, true);
    }

    setQuantityFromResponse(response, fallback) {
      var quantity = fallback || 0;
      if (response && response.items && Array.isArray(response.items)) {
        for (var i = 0; i < response.items.length; i++) {
          if (String(response.items[i].variant_id) === String(this.variantId)) {
            quantity = response.items[i].quantity;
            break;
          }
        }
      }
      this.setQuantity(quantity, true);
    }

    setQuantity(quantity, skipSync) {
      this.quantity = Math.max(0, parseInt(quantity, 10) || 0);
      this.updateQuantityDisplay();
      this.showQuantitySelector(this.quantity > 0);
    }

    showQuantitySelector(show) {
      if (!this.quantityWrapper || !this.formElement) return;
      this.quantityWrapper.hidden = !show;
      if (this.addButton) this.addButton.hidden = show;
      this.formElement.hidden = show;
      this.rootElement.setAttribute('data-in-cart', show ? 'true' : 'false');
      this.updateQuantityDisplay();
    }

    showAddButton() {
      this.showQuantitySelector(false);
    }

    updateQuantityDisplay() {
      if (this.quantityElement) this.quantityElement.textContent = this.quantity;
      if (this.formQuantity) this.formQuantity.value = this.quantity > 0 ? this.quantity : 1;
    }

    renderCart(response) {
      if (!response || !response.sections) return;
      var cartElement = this.getCartElement();
      if (!cartElement || typeof cartElement.renderContents !== 'function') return;

      if (cartElement.classList.contains('is-empty')) cartElement.classList.remove('is-empty');
      cartElement.renderContents(response);
    }

    publishUpdate(response) {
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'napc-cart-control',
            productVariantId: Number(this.variantId),
            cartData: response
          });
        }
      } catch (error) {
        console.error('[NAPC] Publish error:', error);
      }

      try {
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              cart: response,
              variantId: Number(this.variantId),
              source: 'napc-cart-control'
            }
          })
        );
      } catch (error) {
        console.error('[NAPC] Custom event error:', error);
      }
    }

    setBusy(state) {
      this.busy = state;
      if (this.addButton) this.addButton.disabled = state;
      if (this.buttonText) this.buttonText.hidden = state;
      if (this.loadingText) this.loadingText.hidden = !state;
      if (this.minusButton) this.minusButton.disabled = state;
      if (this.plusButton) this.plusButton.disabled = state;
    }

    setLoading(state) { this.setBusy(state); }
    setQuantityLoading(state) { this.setBusy(state); }
  }


  /*
   * =========================================================
   * INITIALIZATION
   * =========================================================
   */
  function initializeNapcCartControls(scope) {
    scope = scope || document;
    if (!scope || !scope.querySelectorAll) return;

    window.NAPCLiveCart.init();

    scope.querySelectorAll('.napc-cart-control').forEach(function (element) {
      if (element.dataset.napcInitialized === 'true') return;
      element.dataset.napcInitialized = 'true';
      new NapcCartControl(element);
    });
  }

  // The script is `defer`, so the DOM is already parsed here. Controls must be
  // wired immediately so a quick tap never falls back to a native form submit.
  initializeNapcCartControls();

  document.addEventListener('shopify:section:load', function (event) {
    initializeNapcCartControls(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    initializeNapcCartControls(event.target);
  });

  window.initializeNapcCartControls = initializeNapcCartControls;
})();